// src/pages/SubcodonProfile/index.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Button, ButtonGroup, Alert } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";

// Common Components (Our new reusable components)
import GeneralSettings from "../../Components/Common/GeneralSettings";
import Files from "../../Components/Common/Files";
import SelectedFilesDisplay from "../../Components/Common/SelectedFilesDisplay";
import PlotContainer from "../../Components/Common/PlotContainer";
import SeqTypesAndGroups from "../../Components/Common/SeqTypesAndGroups";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import ActionButton from "../../Components/Common/ActionButton";
import TranscriptChooserModal from "../../Components/Common/TranscriptChooserModal";

// API
import {
  getInteractivePlotData,
  generateSubcodonPlot,
} from "../../helpers/eirna_backend_helper";

// Hooks (Our new reusable hooks)
import { useSettings } from "../../hooks/useSettings";
import { useFileSelection } from "../../hooks/useFileSelection";
import { useSmartReadLength } from "../../hooks/useSmartReadLength";
import { EnterButtonListener} from "../../hooks/EnterButtonListener";
import { useDefaultFileSelection } from "../../hooks/useDefaultFileSelection";
import { useScrollToRef } from "../../hooks/useScrollToRef";

// Configuration (Our new config-driven approach)
import {
  subcodonProfileConfig,
  subcodonProfileDefaults,
  subcodonProfileValidation,
} from "../../config/plotConfigs/subcodonProfileConfig";

// Short code handling
import {
  useSubcodonProfileShortCode,
  createPlotSpecificPayload,
} from "../../hooks/useShortCodeHandlers";

import { FadeLoader } from "react-spinners";

const SubcodonProfile = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // State management
  const [loading, setLoading] = useState(true);
  const [plotLoading, setPlotLoading] = useState(false);
  const [plotData, setPlotData] = useState(null);
  const [error, setError] = useState(null);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [tableHeaders, setTableHeaders] = useState(null); 
  const [tableRows, setTableRows] = useState([]);

  // Reference points to the plot container, to allow it to scroll on plot completion
  const plotContainerRef  = useRef(null);

  // API data
  const [apiData, setApiData] = useState({
    json_files: [],
    default_tran: "",
    seq_types: [],
    studies_dict: {},
    subgroup_present: false,
  });

  // Enhanced hooks with reusable components
  const {
    settings,
    updateSetting,
    resetSettings,
    validateSettings,
    updateMultipleSettings,
  } = useSettings(subcodonProfileDefaults, subcodonProfileValidation);

  const {
    selectedFiles,
    selectedSeqType,
    selectedGroup,
    toggleFile,
    toggleAllFiles,
    setSelectedSeqType,
    setSelectedGroup,
    clearSelection,
    selectMultipleFiles,
    deselectMultipleFiles,
    setSelectedFiles,
  } = useFileSelection("riboseq");

  const {
    getStandardReadLengths,
    markFieldAsUserModified,
    resetUserModifications,
    shouldAutoAdjust,
  } = useSmartReadLength();

  // Use the Subcodon Profile specific short code handler
  const { userPassedShortCode, autoRunPlot, setAutoRunPlot } =
    useSubcodonProfileShortCode({
      location,
      updateMultipleSettings,
      setSelectedFiles,
    });

  const shortCodeRef = useRef(userPassedShortCode);
  useEffect(() => {
    shortCodeRef.current = userPassedShortCode;
  }, [userPassedShortCode]);

  // A callback used by the EnterButtonListener to generate the plot
  const runPlot = useCallback(() => {
    if (plotLoading) return;
    setAutoRunPlot(true);
  }, [plotLoading]);

  // Listens for the Enter button being pressed, if so calls runPlot (as long as
  // a textarea is not in focus)
  EnterButtonListener({
    onEnter: runPlot,
    modalOpen: chooserOpen,
    enabled: true,
  });

  // Check all riboseq checkboxes by default (unless files passed in URL params)
  useDefaultFileSelection({apiData,
                           selectMultipleFiles,
                           fileType: "riboseq",
                           queryParam: "files"})

  // Scroll to plot/table anytime plotData changes
  useScrollToRef(plotContainerRef, plotData);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getInteractivePlotData(projectId);
        setApiData(response);
        console.log("response", response)


        if (response.default_tran && !shortCodeRef.current) {
          updateSetting("transcript", response.default_tran);
        }
      

        // Set default seq type with preference for riboseq
        const fileTypes = [
          ...new Set(response.json_files?.map((file) => file.file_type) || []),
        ];

        const defaultSeqType = fileTypes.includes("riboseq")
          ? "riboseq"
          : fileTypes[0];
        if (defaultSeqType) {
          setSelectedSeqType(defaultSeqType);
        }

        const uniqueGroups = [
          ...new Set(
            response.json_files?.map((file) => file.subgroup).filter(Boolean) ||
              []
          ),
        ];
        if (uniqueGroups.length > 0) {
          setSelectedGroup(uniqueGroups[0]);
        }

        // Set any values passed as url parameters
        const params = new URLSearchParams(location.search);
        // An array of id's of text parameters that can be passed
        const textURLParams = ["transcript", 
                               "highlightStart",
                               "highlightStop"];

        for (let textURLParam of textURLParams) {
          const paramVal = params.get(textURLParam);
          if (paramVal) {
            updateSetting(textURLParam, paramVal);
          };
        }

        // An array of id's of integer parameters that can be passed
        const intURLParams = ["minRead",
                              "maxRead"];

        for (let intURLParam of intURLParams) {
          const paramVal = params.get(intURLParam);
          if (paramVal) {
            updateSetting(intURLParam, Number(paramVal));
          };
        }

        const filesParamVal = params.get('files');
        if (filesParamVal) {
          const filesParamArray = filesParamVal.split(',').map(Number);
          if (filesParamArray) {
            selectMultipleFiles(filesParamArray);
          };
          setAutoRunPlot(true);
        }

      } catch (err) {
        setError("Failed to load project data");
        toast.error("Failed to load project data");
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      fetchData();
    }
  }, [projectId, updateSetting, setSelectedSeqType, setSelectedGroup]);

  // Auto-trigger plot generation when autoRunPlot is true
  useEffect(() => {
    if (autoRunPlot) {
      console.log("Auto-run triggered, checking conditions...", {
        autoRunPlot,
        plotLoading,
        loading,
        selectedFilesCount: selectedFiles.length,
      });

      if (!plotLoading && !loading && selectedFiles.length > 0) {
        console.log("All conditions met, generating plot from short code...");
        setAutoRunPlot(false);
        setTimeout(() => {
          handleGeneratePlot();
        }, 50);
      } else {
        console.log(
          "Conditions not met yet, will retry when conditions change"
        );
      }
    }
  }, [autoRunPlot, plotLoading, loading, selectedFiles.length, setAutoRunPlot]);

  // Filter files based on selected seq type and group
  const filteredFiles = useMemo(() => {
    let files = apiData.json_files || [];
    if (selectedSeqType && selectedSeqType !== "all") {
      files = files.filter((file) => file.file_type === selectedSeqType);
    }
    if (selectedGroup && selectedGroup !== "all") {
      files = files.filter((file) => file.subgroup === selectedGroup);
    }
    return files;
  }, [apiData.json_files, selectedSeqType, selectedGroup]);

  // Get unique seq types and groups
  const seqTypes = useMemo(() => {
    const types = [
      ...new Set(apiData.json_files?.map((file) => file.file_type) || []),
    ];
    return types.map((type) => ({
      value: type,
      label: type,
      count:
        apiData.json_files?.filter((file) => file.file_type === type).length ||
        0,
    }));
  }, [apiData.json_files]);

  const groups = useMemo(() => {
    const groupSet = new Set(
      apiData.json_files?.map((file) => file.subgroup) || []
    );
    return Array.from(groupSet)
      .filter((group) => group)
      .map((group) => ({
        value: group,
        label: group,
        count:
          apiData.json_files?.filter((file) => file.subgroup === group)
            .length || 0,
      }));
  }, [apiData.json_files]);

  // Check if all files of selected seq type are selected
  const allSeqTypeFilesSelected = useMemo(() => {
    if (!selectedSeqType || selectedSeqType === "all") return false;
    // Use all files, not filtered files, and filter only by seq type
    const seqTypeFiles = (apiData.json_files || []).filter(
      (file) => file.file_type === selectedSeqType
    );
    return (
      seqTypeFiles.length > 0 &&
      seqTypeFiles.every((file) => selectedFiles.includes(file.file_id))
    );
  }, [selectedSeqType, apiData.json_files, selectedFiles]);

  // Check if all files of selected group are selected
  const allGroupFilesSelected = useMemo(() => {
    if (!selectedGroup || selectedGroup === "all") return false;
    const groupFiles = filteredFiles.filter(
      (file) => file.subgroup === selectedGroup
    );
    return (
      groupFiles.length > 0 &&
      groupFiles.every((file) => selectedFiles.includes(file.file_id))
    );
  }, [selectedGroup, filteredFiles, selectedFiles]);

  // Enhanced setting update handler with smart adjustment tracking
  const handleSettingUpdate = useCallback(
    (key, value) => {
      updateSetting(key, value);
      markFieldAsUserModified(key);
    },
    [updateSetting, markFieldAsUserModified]
  );

  // Enhanced reset handler
  const handleReset = useCallback(() => {
    resetSettings();
    resetUserModifications();
    toast.info("Settings reset to defaults");
  }, [resetSettings, resetUserModifications]);

  // Handle select all seq type files
  const handleSelectAllSeqTypeFiles = useCallback(
    (checked) => {
      if (!selectedSeqType || selectedSeqType === "all") return;

      // Use all files, not filtered files, and filter only by seq type
      const seqTypeFiles = (apiData.json_files || []).filter(
        (file) => file.file_type === selectedSeqType
      );
      const seqTypeFileIds = seqTypeFiles.map((file) => file.file_id);

      if (checked) {
        selectMultipleFiles(seqTypeFileIds);
      } else {
        deselectMultipleFiles(seqTypeFileIds);
      }
    },
    [
      selectedSeqType,
      apiData.json_files,
      selectMultipleFiles,
      deselectMultipleFiles,
    ]
  );

  // Handle select all group files
  const handleSelectAllGroupFiles = useCallback(
    (checked) => {
      if (!selectedGroup || selectedGroup === "all") return;

      const groupFiles = filteredFiles.filter(
        (file) => file.subgroup === selectedGroup
      );
      const groupFileIds = groupFiles.map((file) => file.file_id);

      if (checked) {
        selectMultipleFiles(groupFileIds);
      } else {
        deselectMultipleFiles(groupFileIds);
      }
    },
    [selectedGroup, filteredFiles, selectMultipleFiles, deselectMultipleFiles]
  );


  // Enhanced handleGeneratePlot with universal short code support
  const handleGeneratePlot = useCallback(async () => {
    if (plotLoading) {
      console.log("Plot generation already in progress, skipping...");
      return;
    }

    const validation = validateSettings();

    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return;
    }

    if (selectedFiles.length === 0) {
      toast.error("Warning, no files selected, generating empty plot");
    }

    try {
      setPlotLoading(true);
      setError(null);

      // Use the Subcodon Profile specific payload creator
      const plotRequest = createPlotSpecificPayload.subcodonProfile({
        settings,
        selectedFiles,
        projectId,
        userPassedShortCode,
      });

      console.log(
        "Sending plot request with universal payload structure:",
        plotRequest
      );

      const response = await generateSubcodonPlot(plotRequest);

      if (response.header_row) {
        // Show transcript selection table
        setTableHeaders(response.header_row);
        setTableRows(response.result_rows || []);
        setChooserOpen(true);     // open centered popup
        setPlotData(null);
        setError(null);
        toast.info("Select a transcript");
      } else if (response.message) {

        if (response.message === "user logged out") {
          navigate("/login");
          return;
        }
        setError(response.message);
        toast.error(response.message);
      } else if (response.plot) {
        setPlotData(response.plot);
        toast.success("Plot generated successfully");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Failed to generate plot";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setPlotLoading(false);
    }
  }, [
    settings,
    selectedFiles,
    projectId,
    navigate,
    validateSettings,
    plotLoading,
    userPassedShortCode,
  ]);

  // When a user picks a transcript from the transcript table, update the transcript 
  // input box with chosen transcript, close the pop-up and generate the plot
  const handleSelectTranscript = useCallback((selectedTran) => {
    updateSetting("transcript", selectedTran);
    setChooserOpen(false);
    // set AutoRunPlot to true to trigger plot creation
    setAutoRunPlot(true);
  }, [updateSetting, handleGeneratePlot]);

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="loading-overlay">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "75vh",
              }}
            >
              <FadeLoader color="#1A6D52" size={50} />
            </div>
          </div>
        </Container>
      </div>
    );
  }

  document.title = "Sub-codon Profile | EIRNA Bio Connect";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            breadcrumbs={[
              { label: "Home", url: "/dashboard" },
              {
                label: "Select Function",
                url: `/projects/${projectId}/select-function`,
              },
              { label: "Subcodon Position Profile" },
            ]}
          />

          <Row className="mb-4">
            <Col lg={12}>
              <div className="d-flex justify-content-between align-items-center">
                <h4
                  style={{
                    fontSize: "24px",
                    fontWeight: "600",
                    color: "#1f2937",
                    marginBottom: "0",
                  }}
                >
                  SubCodon Position Profile
                  {autoRunPlot && (
                    <span
                      className="badge bg-info ms-2"
                      style={{ fontSize: "12px" }}
                    >
                      Auto-generating...
                    </span>
                  )}
                </h4>

              </div>
            </Col>
          </Row>

          {/* Top Row - General Settings and Seq Types/Groups */}
          <Row className="mb-4">
            {/* Left Card - General Settings */}
            <Col lg={12}>
              <GeneralSettings
                settings={settings}
                onUpdateSetting={handleSettingUpdate}
                onReset={handleReset}
                config={subcodonProfileConfig}
                title="General Settings"
                loading={plotLoading}
                selectedFiles={selectedFiles}
              />
            </Col>
          </Row>
          <Row className="mb-4">
            {/* Right Card - Seq Types and Groups */}
            <Col lg={12}>
              <SeqTypesAndGroups
                seqTypes={seqTypes}
                groups={groups}
                selectedSeqType={selectedSeqType}
                selectedGroup={selectedGroup}
                onSelectSeqType={setSelectedSeqType}
                onSelectGroup={setSelectedGroup}
                onSelectAllSeqTypeFiles={handleSelectAllSeqTypeFiles}
                onSelectAllGroupFiles={handleSelectAllGroupFiles}
                selectedFiles={selectedFiles}
                filteredFiles={filteredFiles}
                allSeqTypeFilesSelected={allSeqTypeFilesSelected}
                allGroupFilesSelected={allGroupFilesSelected}
                showSelectAllCheckboxes={true}
                customTitle="Seq Types & Groups"
                customIcon="ri-tools-fill"
              />
            </Col>
          </Row>

          {/* Files Card */}
          <Row className="mb-3">
            <Col lg={12}>
              <Files
                files={filteredFiles}
                selectedFiles={selectedFiles}
                onToggleFile={toggleFile}
                onSelectAllGroupFiles={handleSelectAllGroupFiles}
                title="Select Files"
                searchable={false}
                selectable={true}
                filesPerPage={30}
                columns={6}
                showFileCount={true}
                customSearchFields={["file_name", "file_description"]}
              />

              
            </Col>
          </Row>

          {/* Selected Files Display */}
          <Row>
            <Col lg={12}>
              <SelectedFilesDisplay
                selectedFiles={selectedFiles}
                allFiles={apiData.json_files}
                title="Selected Files for Subcodon Profile "
                showSearch={true}
                maxDisplayFiles={0}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col lg={12} className="text-end">
              <ActionButton
                onClick={handleGeneratePlot}
                loading={plotLoading}
                buttonType="plot"
              />
            </Col>
          </Row>

          <TranscriptChooserModal
            isOpen={chooserOpen}
            toggle={() => setChooserOpen(false)}
            headers={tableHeaders || []}
            rows={tableRows}
            onSelect={handleSelectTranscript}
            loading={plotLoading}
          />


          {/* Plot Container */}
          {(plotData || plotLoading || error) && (
            <Row>
              <Col lg={12}>
                <PlotContainer
                  ref={plotContainerRef}
                  plotData={plotData}
                  loading={plotLoading}
                  error={error}
                  selectedFilesCount={selectedFiles.length}
                  title="Subcodon Position Profile Results"
                  onRetry={handleGeneratePlot}
                  showFileCount={true}
                />
              </Col>
            </Row>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default SubcodonProfile;
